import {
  formatPanelStateForPrompt,
  getPanelData,
  getPanelLiveState,
} from '@/stores/panel-state-registry'
import type { PanelMention } from '@/stores/chat-store'
import type { CompanionPanelData } from '@/types/schemas/companion-panel-data'
import { resolvePanelTitle } from '@/workspace/panel-title'
import { useActionRegistrationStore } from './action-registration-store'
import { lastPressedOf } from './last-panel-tracker'

/**
 * The panels the user @-mentioned in THIS conversation, attached to every
 * analyst request until the user mentions something else or changes thread.
 * Data the user sends is evidence: it travels as structured `panels`, never
 * as prose the model may discard. Sticky because follow-ups ("summarize why
 * they are moving?") rarely re-mention the panel (2026-10-01: a follow-up
 * lost the scanner and was answered from a market-wide screener).
 */

/** Server cap (analyst/panel-evidence.ts PanelsSchema). */
const MAX_PANELS = 4
/** An idle conversation's panels expire; a new question an hour later is a new topic. */
export const TURN_PANELS_TTL_MS = 30 * 60_000

/** One mentioned panel as data: its registered table, else its live state as notes. */
export function panelDataFor(mention: PanelMention): CompanionPanelData {
  const table = getPanelData(mention.panelId)
  if (table) return { ...table, title: table.title || mention.title.slice(0, 120) }
  const state = getPanelLiveState(mention.panelId)
  const notes = state
    ? formatPanelStateForPrompt(state)
        .split('\n')
        .map((l) => l.trim().slice(0, 300))
        .filter(Boolean)
        .slice(0, 16)
    : []
  return {
    type: (mention.componentType || 'panel').slice(0, 40),
    title: mention.title.slice(0, 120),
    captured_at: new Date().toISOString(),
    columns: [],
    rows: [],
    ...(notes.length ? { notes } : {}),
  }
}

interface Attached {
  mentions: PanelMention[]
  /** The last data read per panel, kept for when the panel is gone. */
  snapshots: CompanionPanelData[]
  at: number
}

let attached: Attached | null = null

/**
 * Called on every send. New mentions REPLACE the attached panels; a plain
 * message keeps them (a follow-up) and only refreshes the idle clock.
 */
export function captureTurnPanels(mentions: PanelMention[], now = Date.now()): void {
  if (mentions.length > 0) {
    const kept = mentions.slice(0, MAX_PANELS)
    attached = { mentions: kept, snapshots: kept.map(panelDataFor), at: now }
  } else if (attached && now - attached.at <= TURN_PANELS_TTL_MS) {
    attached.at = now
  } else {
    attached = null
  }
}

/**
 * A panel the trader names by its kind: "my scanner", "this chart". Every kind
 * the server's missing-panel notice reads (analyst/panel-evidence.ts) is here
 * with the same determiners, so a panel it would miss is attached whenever it
 * is on screen. Never "the news": that is how traders ask for news.
 */
const NAMED_PANEL_RE =
  /\b(?:my|this|that|our|the)\s+(multi-?scanner|scanner|scan|watch\s?list|chart|heat\s?map|calendar)\b/gi

/** The panel types a named kind means. */
function typesNamed(word: string): string[] {
  const kind = word.toLowerCase().replace(/[\s-]/g, '')
  if (kind === 'multiscanner') return ['multi-scanner']
  if (kind === 'scanner' || kind === 'scan') return ['scanner', 'multi-scanner']
  if (kind === 'heatmap') return ['sector-heatmap']
  return [kind]
}

/** The panel types each kind named in `text` means, in order. */
function namedTypes(text: string): string[][] {
  return [...text.matchAll(NAMED_PANEL_RE)].map((m) => typesNamed(m[1]!))
}

/**
 * The panels of `types` on screen now: listed by the active page's callbacks
 * (the layout showing, the list the filter and column tools pick a panel
 * from), not a tab behind another, and publishing data. Another layout is
 * never reached: it is not mounted, so it has nothing to send.
 */
function onScreen(types: string[]): Array<{ id: string; type: string }> {
  const callbacks = useActionRegistrationStore.getState().callbacks
  const byType = callbacks?.getPanelsByType
  if (!byType) return []
  const shown = (id: string) => (callbacks?.activePanelOf?.(id) ?? id) === id
  return types.flatMap((type) =>
    byType(type)
      .map((p) => p.panelId)
      .filter((id) => shown(id) && (getPanelData(id) !== null || getPanelLiveState(id) !== null))
      .map((id) => ({ id, type })),
  )
}

/**
 * The on-screen panels a message names without an @-mention (2026-10-18: "Pick
 * one from my scanner" went without the scanner, and the answer named a ticker
 * from a market-wide list as the scanner's pick). Per kind named: the panel
 * the trader last pressed, as the filter tools pick one, else every one on
 * screen, and with several the analyst asks which (analyst/panel-evidence.ts
 * on the server); at most MAX_PANELS.
 */
export function panelsNamedIn(text: string): PanelMention[] {
  const picked: Array<{ id: string; type: string }> = []
  for (const types of namedTypes(text)) {
    const candidates = onScreen(types)
    const pressed = lastPressedOf(candidates.map((c) => c.id))
    for (const c of pressed ? candidates.filter((x) => x.id === pressed) : candidates)
      if (!picked.some((p) => p.id === c.id)) picked.push(c)
  }
  return picked.slice(0, MAX_PANELS).map((p) => ({
    panelId: p.id,
    componentType: p.type,
    title: resolvePanelTitle(p.id),
  }))
}

/** Detach the attached panels of `types`, keeping the rest. */
function dropAttached(types: string[], now: number): void {
  if (!attached || now - attached.at > TURN_PANELS_TTL_MS) {
    attached = null
    return
  }
  const { mentions, snapshots } = attached
  const keep = mentions.flatMap((m, i) =>
    types.includes(m.componentType) || types.includes(snapshots[i]!.type) ? [] : [i],
  )
  attached = keep.length
    ? {
        mentions: keep.map((i) => mentions[i]!),
        snapshots: keep.map((i) => snapshots[i]!),
        at: now,
      }
    : null
}

/**
 * On every send: the panels @-mentioned, else the ones the message names, as
 * if they were @-mentioned. A kind it names with none on screen drops one
 * attached earlier (from another layout, or since closed), which must not
 * stand in for it; the server then says it cannot see it. A message naming
 * none keeps what is attached.
 */
export function captureMessagePanels(
  text: string,
  mentions: PanelMention[],
  now = Date.now(),
): void {
  if (mentions.length > 0) {
    captureTurnPanels(mentions, now)
    return
  }
  const named = namedTypes(text)
  const found = named.length > 0 ? panelsNamedIn(text) : []
  if (found.length > 0) captureTurnPanels(found, now)
  else if (named.length > 0) dropAttached(named.flat(), now)
  else captureTurnPanels([], now)
}

/** New chat or a different thread: nothing carries over. */
export function clearTurnPanels(): void {
  attached = null
}

/**
 * The conversation's panels as of NOW: re-read from the live panel (fresh
 * rows), falling back to the last snapshot when the panel was closed.
 */
export function currentTurnPanels(now = Date.now()): CompanionPanelData[] | null {
  if (!attached || now - attached.at > TURN_PANELS_TTL_MS) return null
  const current = attached
  current.snapshots = current.mentions.map((m, i) => {
    const live = getPanelData(m.panelId)
    return live ? { ...live, title: live.title || m.title.slice(0, 120) } : current.snapshots[i]!
  })
  return current.snapshots
}
